import type Highcharts from 'highcharts';
import HCExportData from 'highcharts/modules/export-data';
import moment from 'moment';
import zipcelx from 'zipcelx';
import { isLegendAxisDivider } from './doubleYAxisLegend';

type HighchartsWithMap = typeof Highcharts & {
  map: <T, U>(arr: T[], fn: (item: T, index: number) => U) => U[];
};

const REGISTERED = new WeakSet<object>();
const SUPPRESS_SELECTION_SYNC_KEY = 'suppressLineChartSelectionSync';

type ChartOptionsWithCustom = Highcharts.Options & {
  custom?: Record<string, unknown>;
};

export function isLineChartSelectionSyncSuppressed(chart: Highcharts.Chart | undefined): boolean {
  return Boolean((chart?.options as ChartOptionsWithCustom | undefined)?.custom?.[SUPPRESS_SELECTION_SYNC_KEY]);
}

export function setLineChartSelectionSyncSuppressed(chart: Highcharts.Chart, suppressed: boolean) {
  const options = chart.options as ChartOptionsWithCustom;
  if (!options.custom) {
    options.custom = {};
  }

  if (suppressed) {
    options.custom[SUPPRESS_SELECTION_SYNC_KEY] = true;
    return;
  }

  delete options.custom[SUPPRESS_SELECTION_SYNC_KEY];
}

function isExportableSeries(series: Highcharts.Series): boolean {
  const name = series.name;
  if (!name || /^Navigator \d+$/.test(name) || isLegendAxisDivider(name)) {
    return false;
  }
  if ((series.options as { custom?: { isMeanAggregate?: boolean } })?.custom?.isMeanAggregate) {
    return false;
  }
  return true;
}

function extractColumnTitle(cell: unknown): string {
  if (typeof cell === 'string') {
    return cell;
  }
  if (cell && typeof cell === 'object' && 'columnTitle' in cell) {
    return String((cell as { columnTitle?: string }).columnTitle ?? '');
  }
  return String(cell ?? '');
}

function getLegendSeriesColumnOrder(chart: Highcharts.Chart, includeHiddenSeries: boolean): string[] {
  const reversed = chart.options.legend?.reversed === true;

  let series = chart.series.filter(candidate => {
    if (!isExportableSeries(candidate)) {
      return false;
    }
    if (candidate.options?.showInLegend === false) {
      return false;
    }
    if (!includeHiddenSeries && candidate.visible === false) {
      return false;
    }
    return true;
  });

  if (reversed) {
    series = [...series].reverse();
  }

  return series.map(candidate => candidate.name).filter((name): name is string => Boolean(name));
}

function getExportColumnOrder(headerRow: unknown[], legendSeriesOrder: string[]): number[] | null {
  if (!legendSeriesOrder.length || !headerRow.length) {
    return null;
  }

  const titles = headerRow.map(extractColumnTitle);
  const seriesColumnByName = new Map<string, number>();

  titles.forEach((title, index) => {
    if (legendSeriesOrder.includes(title)) {
      seriesColumnByName.set(title, index);
    }
  });

  const orderedSeriesIndices = legendSeriesOrder
    .map(name => seriesColumnByName.get(name))
    .filter((index): index is number => index !== undefined);

  if (orderedSeriesIndices.length <= 1) {
    return null;
  }

  const seriesIndexSet = new Set(orderedSeriesIndices);
  const categoryIndices = titles.map((_, index) => index).filter(index => !seriesIndexSet.has(index));

  return [...categoryIndices, ...orderedSeriesIndices];
}

function removeLegendDividerColumns(chart: Highcharts.Chart, rows: unknown[][]): unknown[][] {
  if (!rows.length) {
    return rows;
  }

  const useMultiLevelHeaders = chart.options.exporting?.useMultiLevelHeaders !== false;
  const headerRowCount = useMultiLevelHeaders ? 2 : 1;
  const dividerIndexes = new Set<number>();

  for (let rowIndex = 0; rowIndex < Math.min(headerRowCount, rows.length); rowIndex += 1) {
    const row = rows[rowIndex];
    if (!Array.isArray(row)) {
      continue;
    }

    row.forEach((cell, index) => {
      if (isLegendAxisDivider(extractColumnTitle(cell))) {
        dividerIndexes.add(index);
      }
    });
  }

  if (!dividerIndexes.size) {
    return rows;
  }

  return rows.map(row => (Array.isArray(row) ? row.filter((_, index) => !dividerIndexes.has(index)) : row));
}

function reorderExportDataRows(chart: Highcharts.Chart, rows: unknown[][], includeHiddenSeries: boolean): unknown[][] {
  if (!rows.length) {
    return rows;
  }

  const legendSeriesOrder = getLegendSeriesColumnOrder(chart, includeHiddenSeries);
  const useMultiLevelHeaders = chart.options.exporting?.useMultiLevelHeaders !== false;
  const headerRowCount = useMultiLevelHeaders ? 2 : 1;
  const headerRow = rows[Math.min(headerRowCount, rows.length) - 1];

  if (!Array.isArray(headerRow)) {
    return rows;
  }

  const columnOrder = getExportColumnOrder(headerRow, legendSeriesOrder);
  if (!columnOrder) {
    return rows;
  }

  return rows.map(row => (Array.isArray(row) ? columnOrder.map(index => row[index]) : row));
}

function prepareExportDataRows(chart: Highcharts.Chart, rows: unknown[][], includeHiddenSeries: boolean): unknown[][] {
  return reorderExportDataRows(chart, removeLegendDividerColumns(chart, rows), includeHiddenSeries);
}

function formatRowsAsCsv(chart: Highcharts.Chart, rows: unknown[][], useLocalDecimalPoint = true): string {
  const csvOptions = chart.options.exporting?.csv ?? {};
  const decimalPoint =
    csvOptions.decimalPoint ??
    (csvOptions.itemDelimiter !== ',' && useLocalDecimalPoint ? (1.1).toLocaleString().substring(1, 2) : '.');
  const itemDelimiter = csvOptions.itemDelimiter ?? (decimalPoint === ',' ? ';' : ',');
  const lineDelimiter = csvOptions.lineDelimiter ?? '\n';
  const columnCount = rows.length ? rows[0].length : 0;

  return rows
    .map(row => {
      const formattedRow = [...row];
      for (let columnIndex = formattedRow.length; columnIndex--; ) {
        let value = formattedRow[columnIndex];
        if (value !== null && typeof value === 'object') {
          value = extractColumnTitle(value);
        }
        if (typeof value === 'string') {
          value = `"${value}"`;
        }
        if (typeof value === 'number' && decimalPoint !== '.') {
          value = value.toString().replace('.', decimalPoint);
        }
        formattedRow[columnIndex] = value;
      }
      formattedRow.length = columnCount;
      return formattedRow.join(itemDelimiter);
    })
    .join(lineDelimiter);
}

function runWithAllSeriesVisible(chart: Highcharts.Chart, callback: () => void) {
  const targets = chart.series.filter(isExportableSeries);
  const previousVisibility = targets.map(series => [series, series.visible] as const);

  setLineChartSelectionSyncSuppressed(chart, true);

  try {
    targets.forEach(series => series.setVisible(true, false));
    chart.redraw(false);
    callback();
  } finally {
    previousVisibility.forEach(([series, visible]) => series.setVisible(visible, false));
    chart.redraw(false);
    setLineChartSelectionSyncSuppressed(chart, false);
  }
}

function triggerCsvDownload(csv: string, filename: string) {
  const link = document.createElement('a');
  link.download = filename;
  link.style.display = 'none';

  if (typeof URL.createObjectURL === 'function') {
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    link.href = url;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    return;
  }

  link.href = `data:text/csv;charset=utf-8,\uFEFF${encodeURIComponent(csv)}`;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

function getChartExportFilename(chart: Highcharts.Chart): string {
  const getFilename = (chart as Highcharts.Chart & { getFilename?: () => string }).getFilename;
  if (typeof getFilename === 'function') {
    return getFilename.call(chart);
  }
  return String(chart.options.exporting?.filename ?? 'chart');
}

function downloadCSVFromChart(chart: Highcharts.Chart, includeHiddenSeries: boolean) {
  const getDataRows = chart.getDataRows;
  if (typeof getDataRows !== 'function') {
    return;
  }

  const rows = prepareExportDataRows(chart, getDataRows.call(chart, true), includeHiddenSeries);
  const useMultiLevelHeaders = chart.options.exporting?.useMultiLevelHeaders !== false;
  const csvRows = useMultiLevelHeaders ? rows.slice(1) : rows;
  const csv = formatRowsAsCsv(chart, csvRows, true);
  triggerCsvDownload(csv, `${getChartExportFilename(chart)}.csv`);
}

function downloadXLSFromChart(chart: Highcharts.Chart, H: HighchartsWithMap, includeHiddenSeries: boolean) {
  const getDataRows = chart.getDataRows;
  if (typeof getDataRows !== 'function') {
    return;
  }

  const rows = prepareExportDataRows(chart, getDataRows.call(chart, true), includeHiddenSeries);
  const xlsxRows = H.map(rows.slice(1), (row: unknown[]) =>
    H.map(row, (column: unknown) => {
      let tempColumn = column;
      if (typeof column === 'string' && column.includes('00:00:00')) {
        tempColumn = moment(column).format('MM/DD/YYYY');
      }
      return {
        type: typeof column === 'number' ? 'number' : 'string',
        value: tempColumn,
      };
    }),
  );

  const name = chart.options.exporting?.filename;
  zipcelx({
    filename: name as string,
    sheet: {
      data: xlsxRows,
    },
  });
}

export function registerLineChartExport(H: HighchartsWithMap) {
  if (REGISTERED.has(H)) {
    return;
  }

  HCExportData(H);

  H.Chart.prototype.downloadXLSSelected = function downloadXLSSelected(this: Highcharts.Chart) {
    downloadXLSFromChart(this, H, false);
  };

  H.Chart.prototype.downloadXLSAll = function downloadXLSAll(this: Highcharts.Chart) {
    runWithAllSeriesVisible(this, () => downloadXLSFromChart(this, H, true));
  };

  H.Chart.prototype.downloadCSVSelected = function downloadCSVSelected(this: Highcharts.Chart) {
    downloadCSVFromChart(this, false);
  };

  H.Chart.prototype.downloadCSVAll = function downloadCSVAll(this: Highcharts.Chart) {
    runWithAllSeriesVisible(this, () => downloadCSVFromChart(this, true));
  };

  REGISTERED.add(H);
}

export type LineChartExportMenuItem =
  | string
  | {
      text: string;
      onclick(this: Highcharts.Chart): void;
    };

const LINE_CHART_EXPORT_BASE_MENU_ITEMS: LineChartExportMenuItem[] = [
  'viewFullscreen',
  'downloadPNG',
  'downloadSVG',
  'downloadPDF',
];

const LINE_CHART_EXPORT_SELECTED_MENU_ITEMS: LineChartExportMenuItem[] = [
  {
    text: 'Download Selected XLS',
    onclick: function handleDownloadSelectedXLS(this: Highcharts.Chart) {
      this.downloadXLSSelected();
    },
  },
  {
    text: 'Download Selected CSV',
    onclick: function handleDownloadSelectedCSV(this: Highcharts.Chart) {
      this.downloadCSVSelected();
    },
  },
];

const LINE_CHART_EXPORT_ALL_MENU_ITEMS: LineChartExportMenuItem[] = [
  {
    text: 'Download All XLS',
    onclick: function handleDownloadAllXLS(this: Highcharts.Chart) {
      this.downloadXLSAll();
    },
  },
  {
    text: 'Download All CSV',
    onclick: function handleDownloadAllCSV(this: Highcharts.Chart) {
      this.downloadCSVAll();
    },
  },
];

export function getLineChartExportMenuItems(exportableSeriesCount: number): LineChartExportMenuItem[] {
  const items: LineChartExportMenuItem[] = [...LINE_CHART_EXPORT_BASE_MENU_ITEMS, 'separator'];

  if (exportableSeriesCount > 1) {
    items.push(...LINE_CHART_EXPORT_SELECTED_MENU_ITEMS, 'separator');
  }

  items.push(...LINE_CHART_EXPORT_ALL_MENU_ITEMS);
  return items;
}

export const LINE_CHART_EXPORT_MENU_ITEMS = getLineChartExportMenuItems(2);

declare module 'highcharts' {
  interface Chart {
    downloadXLSSelected(): void;
    downloadXLSAll(): void;
    downloadCSVSelected(): void;
    downloadCSVAll(): void;
  }
}
